// 解析媒体详情数据的工具函数

import { isAdmin } from '@/lib/permissions';

/**
 * 解析媒体项详情数据
 * @param {Object} item - 媒体项数据
 * @param {String} source - 数据来源，可以是 'history' 或 'explore'
 * @param {Object} currentUser - 当前用户信息
 * @returns {Object} 处理后的媒体详情数据
 */
export const parseMediaDetails = (item, source = 'history', currentUser = null) => {
  if (!item) return null;
  
  try {
    // 根据数据来源选择不同的解析逻辑
    if (source === 'history') {
      return parseHistoryDetails(item, currentUser);
    } else if (source === 'explore') {
      return parseExploreDetails(item, currentUser);
    }
    
    // 默认使用 history 的解析逻辑
    return parseHistoryDetails(item, currentUser);
  } catch (error) {
    console.error('Error parsing media details:', error);
    return null;
  }
}

/**
 * 检查用户是否有查看完整信息的权限
 * @param {Object} item - 媒体项数据
 * @param {Object} currentUser - 当前用户信息
 * @returns {Boolean} 是否有权限
 */
const hasFullAccessPermission = (item, currentUser) => {
  // 未登录用户没有权限
  if (!currentUser) return false;
  
  // 管理员拥有所有权限
  if (isAdmin(currentUser.email)) return true;
  
  return false;
}

/**
 * 从原始数据中提取用户友好的数据
 * @param {Object} data - 原始数据
 * @param {Boolean} isImage - 是否为图片
 * @returns {Object} 用户友好的数据
 */
const extractUserFriendlyData = (data, isImage) => {
  if (!data) return {};

  try {
    // 确保data是对象
    const dataObj = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (isImage) {
      // 提取图片相关的用户友好数据
      // 处理新版结构，包含input和output的情况
      if (dataObj.input) {
        const input = dataObj.input;
        return {
          // 基本参数
          width: input.custom_width || input.width || dataObj.width,
          height: input.custom_height || input.height || dataObj.height,
          
          // 生成参数
          seed: input.seed || dataObj.seed,
          guidance_scale: input.guidance || input.guidance_scale || dataObj.guidance_scale,
          steps: input.inference_steps || input.num_inference_steps || dataObj.steps,
          
          // 尺寸相关
          aspect_ratio: input.aspect_ratio,
          megapixels: input.megapixels,
          
          // 质量相关
          output_format: input.output_format,
          output_quality: input.output_quality,
          
          // 其他参数
          model: input.model || dataObj.model,
          negative_prompt: input.negative_prompt || dataObj.negative_prompt,
          safety_checker: input.safety_checker,
          
          // 图生图相关参数
          has_input_image: !!input.input_image_url,
          input_image_url: input.input_image_url,
          prompt_strength: input.prompt_strength,
          image_prompt_strength: input.image_strength,
          
          // 高级参数
          prompt_upsampling: input.prompt_upsampling
        };
      } else {
        // 兼容旧版结构
        return {
          width: dataObj.width,
          height: dataObj.height,
          seed: dataObj.seed,
          steps: dataObj.steps || dataObj.num_inference_steps,
          guidance_scale: dataObj.guidance_scale || dataObj.guidance,
          model: dataObj.model,
          negative_prompt: dataObj.negative_prompt
        };
      }
    } else {
      // 提取视频相关的用户友好数据
      const result = {
        duration: dataObj.duration,
        aspect_ratio: dataObj.aspect_ratio,
        quality: dataObj.quality,
        model: dataObj.model,
        motion_mode: dataObj.motion_mode,
        seed: dataObj.seed,
        negative_prompt: dataObj.negative_prompt,
        
        // 提取更多可能存在的视频参数
        fps: dataObj.fps,
        template_id: dataObj.template_id !== "none" ? dataObj.template_id : null,
        has_audio: !!dataObj.audio_url,
        webhook: dataObj.webhook
      };
      
      // 处理图生视频的情况（输入图片）
      if (dataObj.image_url) {
        result.has_input_image = true;
        result.input_image_url = dataObj.image_url;
        result.is_image_to_video = true;
      }
      
      return result;
    }
  } catch (error) {
    console.error('Error extracting user friendly data:', error);
    return {};
  }
}

/**
 * 解析 GenerationHistory 组件的媒体详情
 * @param {Object} item - 媒体项数据
 * @param {Object} currentUser - 当前用户信息
 * @returns {Object} 处理后的媒体详情数据
 */
const parseHistoryDetails = (item, currentUser) => {
  // 先从 output 检测实际内容类型，而不是仅依赖 type 字段
  let isImage = item.type === 'image'; // 默认值

  // 优先从 output 字段判断内容类型
  if (item.output) {
    try {
      let outputData = null;
      if (typeof item.output === 'string') {
        outputData = JSON.parse(item.output);
      } else if (typeof item.output === 'object') {
        outputData = item.output;
      }

      if (outputData) {
        // WAN22 格式：检测 image 或 video 字段
        if (outputData.image && outputData.image.url) {
          isImage = true;
        } else if (outputData.video && outputData.video.url) {
          isImage = false;
        }
        // 其他格式保持原有 type 判断
      }
    } catch (e) {
      console.warn('Failed to parse output for type detection:', e);
    }
  }

  let mediaUrl = '';
  let prompt = '';
  let timestamp = new Date(item.created_at).toLocaleString();
  let status = item.status;

  // 从 input 字段获取 prompt（统一逻辑）
  if (item.input) {
    let inputData = null;
    if (typeof item.input === 'string') {
      try {
        inputData = JSON.parse(item.input);
      } catch (e) {
        console.warn('Failed to parse input JSON:', e);
        prompt = item.input; // 如果解析失败，可能是直接的 prompt 字符串
      }
    } else if (typeof item.input === 'object') {
      inputData = item.input;
    }

    if (inputData) {
      prompt = inputData.prompt || inputData.text || inputData.description || '';
    }
  }

  if (isImage) {
    // Fallback: 从旧的 data.input.prompt 获取
    if (!prompt) {
      prompt = item.data?.input?.prompt || 'No prompt available';
    }

    // 优先从 output 字段获取图片 URL
    if (item.output) {
      try {
        let outputData = null;
        if (typeof item.output === 'string') {
          outputData = JSON.parse(item.output);
        } else if (typeof item.output === 'object') {
          outputData = item.output;
        }

        if (outputData) {
          // WAN22 图片格式: { image: { url, stored_url } }
          if (outputData.image && outputData.image.url) {
            mediaUrl = outputData.image.url || outputData.image.stored_url || '';
          }
          // 其他格式: { url }
          else if (outputData.url) {
            mediaUrl = outputData.url;
          }
        }
      } catch (e) {
        console.warn('Failed to parse image output:', e);
      }
    }

    // Fallback: 从 data.output.images 获取（旧格式）
    if (!mediaUrl) {
      mediaUrl = item.data?.output?.images?.[0] || '';
    }
  } else {
    const videoData = typeof item.data === 'string'
      ? JSON.parse(item.data)
      : item.data;

    // Fallback: 从 data.prompt 获取
    if (!prompt) {
      prompt = videoData?.prompt || 'No prompt available';
    }
    
    // 获取视频URL，优先从output字段获取
    mediaUrl = '';
    
    // 检查原始类型，特别处理 veo3 类型
    const videoType = item.originalType || item.type;
    
    if (item.output) {
      // 对于 veo3 和 veo3-hf 类型，output 可能是 JSON 字符串
      if ((videoType === 'veo3' || videoType === 'veo3-hf') && typeof item.output === 'string') {
        try {
          const outputData = JSON.parse(item.output);
          mediaUrl = outputData.waterMarkedUrl || outputData.url || '';
        } catch (e) {
          console.warn('Failed to parse veo3/veo3-hf output:', e);
          mediaUrl = item.output || '';
        }
      } else if (typeof item.output === 'object') {
        mediaUrl = item.output.waterMarkedUrl || item.output.url || '';
      } else if (typeof item.output === 'string') {
        mediaUrl = item.output || '';
      }
    }
    
    // 如果output中没有URL，从data字段获取
    if (!mediaUrl) {
      mediaUrl = videoData?.url || '';
    }
  }
  
  const hasFullAccess = hasFullAccessPermission(item, currentUser);
  
  // 获取用户友好的数据
  const userFriendlyData = extractUserFriendlyData(item.data, isImage);
  
  // 基本信息（所有用户都能看到）
  const result = {
    isImage,
    mediaUrl,
    prompt,
    timestamp,
    status,
    taskId: item.task_id,
    hasFullAccess,
    userFriendlyData
  };
  
  // 添加付费状态和特色标识（所有用户都能看到）
  if (currentUser) {
    result.isPaid = item.is_paid;
    result.isFeatured = item.is_featured;
  }
  
  // 只有有权限的用户才能看到完整信息
  if (hasFullAccess) {
    return {
      ...result,
      email: item.email,
      userId: item.user_id,
      rawData: item.data
    };
  }
  
  return result;
}

/**
 * 解析 Explore 组件的媒体详情
 * @param {Object} item - 媒体项数据
 * @param {Object} currentUser - 当前用户信息
 * @returns {Object} 处理后的媒体详情数据
 */
const parseExploreDetails = (item, currentUser) => {
  // 先从 output 检测实际内容类型，而不是仅依赖 type 字段
  let isImage = !item.type || item.type === 'image' || item.type === 'texttoimage' || item.type === 'imagetoimage'; // 默认值

  // 优先从 output 字段判断内容类型
  if (item.output) {
    try {
      let outputData = null;
      if (typeof item.output === 'string') {
        outputData = JSON.parse(item.output);
      } else if (typeof item.output === 'object') {
        outputData = item.output;
      }

      if (outputData) {
        // WAN22 格式：检测 image 或 video 字段
        if (outputData.image && outputData.image.url) {
          isImage = true;
        } else if (outputData.video && outputData.video.url) {
          isImage = false;
        }
        // 其他格式保持原有 type 判断
      }
    } catch (e) {
      console.warn('Failed to parse output for type detection:', e);
    }
  }

  let mediaUrl = '';
  let prompt = '';
  let timestamp = new Date(item.created_at).toLocaleString();
  let status = item.status;

  // 从 input 字段获取 prompt（统一逻辑）
  if (item.input) {
    let inputData = null;
    if (typeof item.input === 'string') {
      try {
        inputData = JSON.parse(item.input);
      } catch (e) {
        console.warn('Failed to parse input JSON:', e);
        prompt = item.input; // 如果解析失败，可能是直接的 prompt 字符串
      }
    } else if (typeof item.input === 'object') {
      inputData = item.input;
    }

    if (inputData) {
      prompt = inputData.prompt || inputData.text || inputData.description || '';
    }
  }

  if (isImage) {
    // 图片数据获取
    // Fallback: 从旧的 data.input.prompt 获取
    if (!prompt) {
      prompt = item.data?.input?.prompt || 'No prompt available';
    }

    // 优先从 output 字段获取图片 URL
    if (item.output) {
      try {
        let outputData = null;
        if (typeof item.output === 'string') {
          outputData = JSON.parse(item.output);
        } else if (typeof item.output === 'object') {
          outputData = item.output;
        }

        if (outputData) {
          // WAN22 图片格式: { image: { url, stored_url } }
          if (outputData.image && outputData.image.url) {
            mediaUrl = outputData.image.url || outputData.image.stored_url || '';
          }
          // 其他格式: { url }
          else if (outputData.url) {
            mediaUrl = outputData.url;
          }
        }
      } catch (e) {
        console.warn('Failed to parse image output:', e);
      }
    }

    // Fallback: 从 data.output.images 获取（旧格式）
    if (!mediaUrl) {
      mediaUrl = item.data?.output?.images?.[0] || '';
    }
  } else {
    // 视频数据获取
    try {
      const videoData = typeof item.data === 'string'
        ? JSON.parse(item.data)
        : item.data;

      // Fallback: 从 data.prompt 获取
      if (!prompt) {
        prompt = videoData?.prompt || 'No prompt available';
      }
      
      // 获取视频URL，优先从output字段获取
      mediaUrl = '';
      
      // 检查原始类型，特别处理 veo3 类型
      const videoType = item.originalType || item.type;
      
      if (item.output) {
        // 对于 veo3 和 veo3-hf 类型，output 可能是 JSON 字符串
        if ((videoType === 'veo3' || videoType === 'veo3-hf') && typeof item.output === 'string') {
          try {
            const outputData = JSON.parse(item.output);
            mediaUrl = outputData.waterMarkedUrl || outputData.url || '';
          } catch (e) {
            console.warn('Failed to parse veo3/veo3-hf output:', e);
            mediaUrl = item.output || '';
          }
        } else if (typeof item.output === 'object') {
          mediaUrl = item.output.waterMarkedUrl || item.output.url || '';
        } else if (typeof item.output === 'string') {
          mediaUrl = item.output || '';
        }
      }
      
      // 如果output中没有URL，从data字段获取
      if (!mediaUrl) {
        mediaUrl = videoData?.url || '';
      }
    } catch (e) {
      console.error('Error parsing video data:', e);
    }
  }
  
  const hasFullAccess = hasFullAccessPermission(item, currentUser);
  
  // 获取用户友好的数据
  const userFriendlyData = extractUserFriendlyData(item.data, isImage);
  
  // 基本信息（所有用户都能看到）
  const result = {
    isImage,
    mediaUrl,
    prompt,
    timestamp,
    status,
    taskId: item.task_id,
    model: item.model,
    hasFullAccess,
    userFriendlyData
  };
  
  // 所有用户都能看到的额外信息（在Explore中）
  if (currentUser) {
    result.isPaid = item.is_paid;
    result.isFeatured = item.is_featured;
  }
  
  // 只有有权限的用户才能看到完整信息
  if (hasFullAccess) {
    return {
      ...result,
      id: item.id,
      email: item.email,
      userId: item.user_id,
      rawData: item.data
    };
  }
  
  return result;
} 